import React, { useRef, forwardRef, useContext } from 'react'
import { Accordion, useAccordionButton, AccordionContext } from 'react-bootstrap'
import { Link, useLocation } from 'react-router-dom'
import { logos } from '@/utils/sidebar-menu-icon'
import { Plus, Trophy } from 'lucide-react'
import cn from 'classnames'
import { useAdHistoryEnabled } from '@/components/campaigns/adHistory/useAdHistoryEnabled'

const CustomToggle = forwardRef(({ children, eventKey, onClick, addClass = '', isActive = false }, ref) => {
    const { activeEventKey } = useContext(AccordionContext)
    const theme = localStorage.getItem('theme') || 'light'
    const isOpen = Array.isArray(activeEventKey) ? activeEventKey.includes(eventKey) : activeEventKey === eventKey

    const toggle = useAccordionButton(eventKey, (evt) => {
        evt.preventDefault()
        onClick?.(!isOpen)
    })

    return (
        <div
            ref={ref}
            className={`${theme === 'light' ? 'nav-link-light' : 'nav-link'} d-flex align-items-center justify-content-between ${addClass} ${isActive ? 'active-parent' : ''}`}
            role="button"
            onClick={toggle}
            style={{ cursor: 'pointer' }}
        >
            {children}
            <i className={`right-icon${isOpen ? ' open' : ''}`}>
                {theme === 'light' ? (
                    <svg className="icon-18" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M7.5 15.5L12.5 10.5L7.5 5.5"
                            stroke="#3564D9"
                            strokeWidth="1.25"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                ) : (
                    <svg
                        className="icon-16"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                )}
            </i>
        </div>
    )
})

CustomToggle.displayName = 'CustomToggle'

const CampaignNav = ({ campaignId }) => {
    const wrapperRef = useRef(null)
    const location = useLocation()
    const theme = localStorage.getItem('theme') || 'light'
    const showWhatWorked = useAdHistoryEnabled(campaignId)

    const isDark = theme === 'dark'
    const isActivePath = (path) => location.pathname === path

    const getIconColor = (path) => {
        if (isActivePath(path)) return isDark ? '#FFFFFF' : 'rgba(53, 100, 217, 1)'
        return isDark ? '#899CBD' : 'rgba(53, 100, 217, 1)'
    }

    const renderLink = (path, title, IconComp, altRoutes = []) => (
        <li className="nav-item hover-bg-transparent hover-text-blue tw:flex tw:items-center tw:gap-2" key={path}>
            <Link
                to={path}
                className={cn('tw:flex tw:items-center tw:gap-2 nav-link', {
                    active:
                        location.pathname === path ||
                        altRoutes.includes(location.pathname) ||
                        (path.includes('ads-manager') && location.pathname.includes('/add-audience')),
                    [`active-${theme}`]:
                        location.pathname === path ||
                        altRoutes.includes(location.pathname) ||
                        (path.includes('ads-manager') && location.pathname.includes('/add-audience')),
                })}
            >
                <IconComp path={path} />
                <span className={`item-name${theme === 'dark' ? '' : '-light'}`}>{title}</span>
            </Link>
        </li>
    )

    const renderAccordion = (eventKey, title, IconComp, submenuItems) => {
        const isChildActive = submenuItems.some(
            ({ path, altRoutes = [] }) => location.pathname === path || altRoutes.includes(location.pathname),
        )

        return (
            <Accordion.Item as="li" className="nav-item" eventKey={eventKey} key={eventKey}>
                <CustomToggle
                    eventKey={eventKey}
                    onClick={() => {}}
                    addClass="hover-bg-transparent hover-text-blue"
                    isActive={isChildActive}
                >
                    <span className="tw:flex tw:items-center tw:w-full tw:gap-2">
                        <IconComp path={location.pathname} />
                        <span className={`item-name${theme === 'dark' ? '' : '-light'}`}>{title}</span>
                    </span>
                </CustomToggle>
                <Accordion.Collapse eventKey={eventKey}>
                    <ul className="sub-nav list-unstyled ps-1">
                        {submenuItems.map(({ path, title, Icon: SubIcon, altRoutes = [] }, idx) => (
                            <li className="nav-item hover-bg-transparent hover-text-blue tw:pl-2" key={path + idx}>
                                <Link
                                    to={path}
                                    className={cn('tw:flex tw:items-center tw:gap-2 nav-link', {
                                        active: location.pathname === path || altRoutes.includes(location.pathname),
                                        [`active-${theme}`]:
                                            location.pathname === path || altRoutes.includes(location.pathname),
                                    })}
                                >
                                    {SubIcon && <SubIcon path={path} />}
                                    <span className={`item-name${theme === 'dark' ? '' : '-light'}`}>{title}</span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </Accordion.Collapse>
            </Accordion.Item>
        )
    }

    const DashboardIcon = () => (
        <img
            src={theme === 'dark' ? logos.Dashboard.dark : logos.Dashboard.light}
            alt="Dashboard"
            className="icon icon-20"
        />
    )
    const InsightsIcon = () => (
        <img
            src={theme === 'dark' ? logos.Insights.dark : logos.Insights.light}
            alt="Insights"
            className="icon icon-20"
        />
    )
    const AutomationIcon = () => (
        <img
            src={theme === 'dark' ? logos.Automation.dark : logos.Automation.light}
            alt="Automation"
            className="icon icon-20"
        />
    )
    const AdsManagerIcon = () => (
        <img
            src={theme === 'dark' ? logos.AddsManager.dark : logos.AddsManager.light}
            alt="Ads Manager"
            className="icon icon-20"
        />
    )
    const CreativesIcon = () => (
        <img
            src={theme === 'dark' ? logos.Creatives.dark : logos.Creatives.light}
            alt="Creatives"
            className="icon icon-20"
        />
    )
    const PlusIcon = () => (
        <img
            src={theme === 'dark' ? logos.NewAdCampaign.dark : logos.NewAdCampaign.light}
            alt="Create Campaign"
            className="icon icon-20"
        />
    )
    const SettingsIcon = () => (
        <img
            src={theme === 'dark' ? logos.MediaSettings.dark : logos.MediaSettings.light}
            alt="Settings"
            className="icon icon-20"
        />
    )

    const TargetingInsightsIcon = ({ path }) => (
        <svg
            className="icon icon-20"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle
                cx="11.793"
                cy="12.207"
                r="3.07829"
                transform="rotate(-45 11.793 12.207)"
                stroke={getIconColor(path, location, theme)}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M19.1851 9.92866C19.6951 11.5836 19.6351 13.3617 19.0147 14.9785C18.3942 16.5952 17.2492 17.957 15.763 18.8458C14.2768 19.7346 12.5353 20.099 10.8174 19.8806C9.09951 19.6622 7.50459 18.8737 6.288 17.6414C5.07142 16.409 4.30355 14.804 4.10734 13.0835C3.91113 11.3629 4.29794 9.62626 5.20583 8.15161C6.11372 6.67697 7.49017 5.54962 9.11479 4.95008C10.7394 4.35053 12.5182 4.31347 14.1664 4.84483"
                stroke={getIconColor(path, location, theme)}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M11.8605 12.1606L16.1889 7.8325M16.1889 7.8325L16.1889 5.71118L18.3102 3.58986V5.71118H20.4315L18.3102 7.8325L16.1889 7.8325Z"
                stroke={getIconColor(path, location, theme)}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
    const AuctionInsightsIcon = ({ path }) => (
        <svg
            className="icon icon-20"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M18.5837 17.8336L13.6339 12.8839M13.6339 12.8839C12.5733 13.9445 11.159 14.6516 9.03773 14.6516L6.56285 12.1768C6.2093 10.409 6.91641 8.99478 8.33062 7.58057M13.6339 12.8839C14.9022 11.4243 15.3594 10.4683 15.4017 8.28768L12.9268 5.8128C11.159 5.45925 9.74484 6.16636 8.33062 7.58057M8.33062 7.58057L6.91641 6.16636"
                stroke={getIconColor(path, location, theme)}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M6 18.5H12.5"
                stroke={getIconColor(path, location, theme)}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
    const GeoDemoInsightsIcon = ({ path }) => <i className="icon bi bi-geo-alt" style={{ color: getIconColor(path) }} />
    const MetaDashboardIcon = ({ path }) => (
        <svg
            className="icon icon-20"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M4.52847 14.1512C4.52847 11.0409 6.08363 7.7953 7.90925 7.7953C8.92349 7.7953 9.73488 8.40384 11.0196 10.2295C9.80249 12.1227 9.05872 13.2722 9.05872 13.2722C7.43594 15.8416 6.89502 16.3825 6.01601 16.3825C5.13701 16.4501 4.52847 15.6387 4.52847 14.1512ZM15.1441 13.0017L13.9947 11.1085C13.7242 10.6352 13.3861 10.1619 13.1157 9.75616C14.1299 8.201 14.9413 7.38961 15.9555 7.38961C17.984 7.38961 19.6068 10.4323 19.6068 14.2188C19.6068 15.6387 19.1335 16.4501 18.1868 16.4501C17.2402 16.4501 16.9021 15.8416 15.1441 13.0017ZM12.2367 8.40385C10.7491 6.44299 9.46441 5.69922 7.97687 5.69922C4.86655 5.69922 2.5 9.82377 2.5 14.1512C2.5 16.8558 3.7847 18.5462 5.9484 18.5462C7.50356 18.5462 8.58541 17.8024 10.6139 14.2864C10.6139 14.2864 11.4253 12.7989 12.0338 11.7846C12.2367 12.1227 12.4395 12.4608 12.6423 12.8665L13.589 14.4893C15.4146 17.5996 16.4288 18.6138 18.2544 18.6138C20.3505 18.6138 21.5 16.8558 21.5 14.0836C21.4324 9.4857 18.9982 5.69922 16.0907 5.69922C14.5356 5.69922 13.3185 6.9163 12.2367 8.40385Z"
                fill={getIconColor(path, location, theme)}
            />
        </svg>
    )

    const AdLibraryIcon = ({ path }) => <i className="bi bi-archive" style={{ color: getIconColor(path) }} />
    const CreativeLibraryIcon = ({ path }) => <i className="bi bi-layers" style={{ color: getIconColor(path) }} />
    const CreativeInsightsIcon = ({ path }) => <i className="bi bi-grid" style={{ color: getIconColor(path) }} />
    const NewAdIcon = ({ path }) => <Plus size={16} style={{ color: getIconColor(path) }} />
    const WhatWorkedIcon = ({ path }) => <Trophy size={16} style={{ color: getIconColor(path) }} />

    return (
        <div
            ref={wrapperRef}
            className={`sidebar sidebar-default campaign-submenu-panel pt-65 ${theme === 'dark' ? 'border-left-white-1' : 'border-left-grey-1'} position-sticky`}
        >
            <Accordion as="ul" className="navbar-nav sidebar-list">
                {/*{renderLink(`/campaigns/media-buying/${campaignId}/dashboard`, 'Dashboard', DashboardIcon)}*/}

                {/*{renderAccordion('insights', 'Insights', InsightsIcon, [*/}
                {/*    {*/}
                {/*        path: `/campaigns/media-buying/${campaignId}/meta-dashboard`,*/}
                {/*        title: 'Meta Dashboard',*/}
                {/*        Icon: MetaDashboardIcon,*/}
                {/*    },*/}
                {/*    {*/}
                {/*        path: `/campaigns/media-buying/${campaignId}/targeting-insights`,*/}
                {/*        title: 'Targeting Insights',*/}
                {/*        Icon: TargetingInsightsIcon,*/}
                {/*    },*/}
                {/*    {*/}
                {/*        path: `/campaigns/media-buying/${campaignId}/auction-insights`,*/}
                {/*        title: 'Auction Insights',*/}
                {/*        Icon: AuctionInsightsIcon,*/}
                {/*    },*/}
                {/*    {*/}
                {/*        path: `/campaigns/media-buying/${campaignId}/geo-demo-insights`,*/}
                {/*        title: 'Geo & Demo Insights',*/}
                {/*        Icon: GeoDemoInsightsIcon,*/}
                {/*    },*/}
                {/*    {*/}
                {/*        path: `/campaigns/media-buying/${campaignId}/creative-insights`,*/}
                {/*        title: 'Creative Insights',*/}
                {/*        Icon: CreativeInsightsIcon,*/}
                {/*    },*/}
                {/*])}*/}

                {/*{renderLink(`/campaigns/media-buying/${campaignId}/automation`, 'Automation', AutomationIcon)}*/}
                {renderLink(`/campaigns/media-buying/${campaignId}/ads-manager`, 'Ads Manager', AdsManagerIcon)}
                {showWhatWorked &&
                    renderLink(
                        `/campaigns/media-buying/${campaignId}/creative-insights`,
                        'What worked',
                        WhatWorkedIcon,
                    )}

                {renderAccordion('creatives', 'Creatives', CreativesIcon, [
                    {
                        path: `/campaigns/media-buying/${campaignId}/ad-library`,
                        title: 'My Creatives',
                        Icon: CreativesIcon,
                        altRoutes: [`/campaigns/media-buying/${campaignId}/creatives/add-to-campaign`],
                    },
                    // {
                    //     path: `/campaigns/media-buying/${campaignId}/creative-library`,
                    //     title: 'Creative Library',
                    //     Icon: CreativeLibraryIcon,
                    // },
                    // {
                    //     path: `/campaigns/media-buying/${campaignId}/creative-insights`,
                    //     title: 'Creative Insights',
                    //     Icon: CreativeInsightsIcon,
                    // },
                    {
                        path: `/campaigns/media-buying/${campaignId}/new-creative`,
                        title: 'New Ad',
                        Icon: NewAdIcon,
                    },
                ])}

                {renderLink(`/campaigns/media-buying/${campaignId}/create-new`, 'New Ad Campaign', PlusIcon)}
                {renderLink(
                    `/campaigns/media-buying/${campaignId}/settings/ad-account`,
                    'Media Settings',
                    SettingsIcon,
                    [`/campaigns/media-buying/${campaignId}/settings/payments`],
                )}
            </Accordion>
        </div>
    )
}

export default CampaignNav
